import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  CircularProgress,
  Box,
  Grid,
  IconButton,
  Paper,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  Collapse,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect, useMemo } from "react";
import {
  getTripolaPlanByIdAction,
  createTripolaPlanAction,
  updateTripolaPlanAction,
} from "../../Actions/TripolaPlanActions";
import CloseIcon from "@mui/icons-material/Close";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import { getPlanFeatureListAction } from "../../Actions/PlanFeatureAction"
import { containsBadWords } from "../../utils/badWord";
import {
  PLAN_FEATURES,
  getEmptyFeatureValues,
  buildFeaturesPayload,
  mapFeaturesToFormValues,
  hasAnyTourSubFeature,
  hasAnyEventSubFeature,
  hasAnyProductSubFeature,
} from "./tripolaPlanFeatures";

const { VALIDATION } = FRONTEND_MESSAGES;

const validationSchema = Yup.object({
  plan_name: Yup.string()
    .trim()
    .matches(/^[a-zA-Z0-9\s&-]+$/, VALIDATION.INVALID_FORMAT("Plan name"))
    .max(50, VALIDATION.MAX_LENGTH("Plan name", 50))
    .required(VALIDATION.REQUIRED_FIELD("Plan name"))
    .test(
      "bad-word",
      "Plan Name contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    ),

  plan_description: Yup.string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Description"))
    .test(
      "bad-word",
      "Plan Description contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    ),

  targeted_users: Yup.string()
    .trim()
    .max(500, VALIDATION.MAX_LENGTH("Targeted Users", 500))
    .required(VALIDATION.REQUIRED_FIELD("Targeted Users"))
    .test(
      "bad-word",
      "Targeted Users Description contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    ),

  monthly_price: Yup.number()
    .typeError(VALIDATION.NUMERIC_ONLY("Monthly price"))
    .positive(VALIDATION.POSITIVE_ONLY("Monthly price"))
    .max(999999999, VALIDATION.MAX_VALUE("Monthly price", 999999999))
    .required(VALIDATION.REQUIRED_FIELD("Monthly price")),

  yearly_price: Yup.number()
    .typeError(VALIDATION.NUMERIC_ONLY("Yearly price"))
    .positive(VALIDATION.POSITIVE_ONLY("Yearly price"))
    .max(999999999, VALIDATION.MAX_VALUE("Yearly price", 999999999))
    .required(VALIDATION.REQUIRED_FIELD("Yearly price")),

  is_active: Yup.boolean(),

  tour_management: Yup.boolean().test(
    "tour-sub-feature-required",
    "Please select at least one Tour Management sub-feature",
    function (value) {
      if (!value) return true;
      return hasAnyTourSubFeature(this.parent);
    }
  ),

  event_management: Yup.boolean().test(
    "event-sub-feature-required",
    "Please select at least one Event Management sub-feature",
    function (value) {
      if (!value) return true;
      return hasAnyEventSubFeature(this.parent);
    }
  ),

  product_management: Yup.boolean().test(
    "product-sub-feature-required",
    "Please select at least one Product Management sub-feature",
    function (value) {
      if (!value) return true;
      return hasAnyProductSubFeature(this.parent);
    }
  ),
});

// Helper: Compare old vs new values (Deep comparison)
const getChangedPlanFields = (oldData, newData) => {
  const changes = {};
  if (!oldData) return changes;

  Object.keys(newData).forEach((key) => {
    const oldVal = oldData[key];
    const newVal = newData[key];

    const oldIsEmpty = oldVal === null || oldVal === undefined || oldVal === "";
    const newIsEmpty = newVal === null || newVal === undefined || newVal === "";

    if (oldIsEmpty && newIsEmpty) return;

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes[key] = {
        old: oldVal,
        new: newVal,
      };
    }
  });

  return changes;
};

const AddEditTripolaPlan = ({
  open,
  handleClose,
  id,
  refetchPlanList = () => { },
  handleToast = () => { },
  changePortalFilter,
  featureData
}) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [planData, setPlanData] = useState(null);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [changedFields, setChangedFields] = useState({});
  const [planPricingData, setPlanPricingData] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [extraCharge, setExtraCharge] = useState(0);
  const [activeFeatureKey, setActiveFeatureKey] = useState("tour_management");
  const [featureSearch, setFeatureSearch] = useState("");
  const [featureFilter, setFeatureFilter] = useState("all"); // all | enabled | disabled
  const planFeatures = PLAN_FEATURES;

  const formik = useFormik({
    initialValues: {
      plan_name: "",
      plan_description: "",
      targeted_users: "",
      ...getEmptyFeatureValues(),
      monthly_price: "",
      yearly_price: "",
      is_active: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (values.tour_management && !hasAnyTourSubFeature(values)) {
        handleToast(
          "error",
          "Please select at least one Tour Management sub-feature"
        );
        return;
      }

      if (values.event_management && !hasAnyEventSubFeature(values)) {
        handleToast(
          "error",
          "Please select at least one Event Management sub-feature"
        );
        return;
      }

      if (values.product_management && !hasAnyProductSubFeature(values)) {
        handleToast(
          "error",
          "Please select at least one Product Management sub-feature"
        );
        return;
      }

      const source =
        featureData && Array.isArray(featureData) && featureData.length
          ? featureData
          : planPricingData;

      const existingFeatureMap = (planData?.feature_snapshot || []).reduce(
        (map, feature) => {
          if (feature.feature_name) {
            map[feature.feature_name] = feature;
          } else if (feature.feature_label) {
            map[feature.feature_label] = feature;
          }
          return map;
        },
        {}
      );

      const featureSnapshot = (source || [])
        .filter((feature) => values[feature.feature_name])
        .map((feature) => {
          const price = parseFloat(feature.price) || 0;

          const oldFeature =
            existingFeatureMap[feature.feature_name] ||
            existingFeatureMap[feature.feature_label];

          const snapshot = {
            feature_name: feature.feature_name,
            feature_label: feature.feature_label,
            price,
            charge: price,
          };

          if (id && oldFeature) {
            const oldPrice =
              parseFloat(oldFeature.price ?? oldFeature.charge ?? 0) || 0;

            snapshot.old_price = oldPrice;
            snapshot.new_price = price;
          }

          return snapshot;
        });

      const payload = {
        plan_name: values.plan_name?.trim(),
        plan_description: values.plan_description?.trim(),
        targeted_users: values.targeted_users?.trim(),
        features: buildFeaturesPayload(values),
        is_extra_charges: extraCharge,
        feature_snapshot: featureSnapshot,
        monthly_price: values.monthly_price,
        yearly_price: values.yearly_price,
        is_active: values.is_active,
      };

      if (id) {
        payload.changedFields = JSON.stringify(Object.keys(changedFields));
        payload.comparisonData = JSON.stringify(changedFields);
      }
      setLoading(true);
      try {
        if (id) {
          const response = await updateTripolaPlanAction(id, payload);
          const { message } = response || {};
          handleToast("success", message || "Plan updated successfully");
        } else {
          const response = await createTripolaPlanAction(payload);
          const { message } = response || {};
          changePortalFilter("tripola");
          handleToast("success", message || "Plan created successfully");
        }
        handleClose();
      } catch (err) {
        const message =
          err?.message ||
          (id
            ? "Something went wrong while updating plan"
            : "Something went wrong while adding plan");

        handleToast("error", message);
        console.error("❌ Error saving plan:", err);
      } finally {
        setLoading(false);
        refetchPlanList();
      }
    },
  });

  useEffect(() => {
    const fetchPlan = async () => {
      if (!id) return;
      setFetching(true);
      try {
        const response = await getTripolaPlanByIdAction(id);
        const data = response?.data?.data || {};
        const features = data.features || {};

        setPlanData(data);

        // formik.resetForm({
        //   values: {
        //     plan_name: data.plan_name || "",
        //     plan_description: data.plan_description || "",
        //     targeted_users: data.targeted_users || "",
        //     tour_management: data.tour_management ?? false,
        //     event_management: data.event_management ?? false,
        //     ticket_management: data.ticket_management ?? false,
        //     vehicle_management: data.vehicle_management ?? false,
        //     product_management: data.product_management ?? false,
        //     coupon_management: data.coupon_management ?? false,
        //     accommodation: data.accommodation ?? false,
        //     monthly_price: data.monthly_price ?? "",
        //     yearly_price: data.yearly_price ?? "",
        //     is_active: data.is_active ?? true,
        //   },
        // });
        const initialValuesToSet = {
          ...formik.initialValues,
          plan_name: data.plan_name || "",
          plan_description: data.plan_description || "",
          targeted_users: data.targeted_users || "",
          ...mapFeaturesToFormValues(features),
          monthly_price: data.monthly_price ?? "",
          yearly_price: data.yearly_price ?? "",
          is_active: data.is_active ?? true,
        };

        formik.resetForm({
          values: initialValuesToSet,
        });
        setInitialFormValues(initialValuesToSet);
      } catch (err) {
        console.error("❌ Error fetching plan:", err);
      } finally {
        setFetching(false);
      }
    };
    fetchPlan();
  }, [id]);

  // Track changed fields for audit/update logging
  useEffect(() => {
    if (!id || !initialFormValues) return;
    const changed = getChangedPlanFields(initialFormValues, formik.values);
    setChangedFields(changed);
  }, [formik.values, initialFormValues, id]);


  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await getPlanFeatureListAction({
          portal_name: "tripolaa"
        });

        // Extract the actual array
        const features = response?.data?.data || [];
        setPlanPricingData(features);
      } catch (err) {
        console.error("Failed to fetch pricing:", err);
        setPlanPricingData([]);
      }
    };

    fetchPricing();
  }, []);


  const handleTextChange = (e) => {
    formik.handleChange(e);
    formik.setFieldTouched(e.target.name, true, false);
  };



  const handleNumberChange = (e) => {
    const value = e.target.value.replace(/\s/g, "");
    formik.setFieldValue(e.target.name, value);
    formik.setFieldTouched(e.target.name, true, false);
  };



  const SectionHeader = ({ title }) => (
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, color: "primary.main", mb: 2 }}
    >
      {title}
    </Typography>
  );

  const PriceBadge = ({ price }) =>
    price && price > 0 ? (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          bgcolor: "#fff7ed",
          border: "1px solid #fed7aa",
          px: 1,
          py: 0.25,
          borderRadius: "999px",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "#c2410c", fontWeight: 700, fontSize: 11, lineHeight: 1.2 }}
        >
          +$ {Number(price).toFixed(2)}
        </Typography>
      </Box>
    ) : null;

  const featureSwitchSx = {
    width: 46,
    height: 28,
    padding: 0,
    "& .MuiSwitch-switchBase": {
      padding: "4px",
      "&.Mui-checked": {
        transform: "translateX(18px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: "#22419e",
          opacity: 1,
          border: "none",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      width: 20,
      height: 20,
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.25)",
    },
    "& .MuiSwitch-track": {
      borderRadius: 14,
      backgroundColor: "#cbd5e1",
      opacity: 1,
    },
  };

  const featureSwitchSmallSx = {
    ...featureSwitchSx,
    width: 40,
    height: 24,
    "& .MuiSwitch-switchBase": {
      padding: "3px",
      "&.Mui-checked": {
        transform: "translateX(16px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: "#22419e",
          opacity: 1,
          border: "none",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      width: 18,
      height: 18,
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.25)",
    },
    "& .MuiSwitch-track": {
      borderRadius: 12,
      backgroundColor: "#cbd5e1",
      opacity: 1,
    },
  };

  const getFeatureLabel = (key) => {
    const findLabel = (features) => {
      for (let f of features) {
        if (f.key === key) return f.label;

        if (f.subcategories) {
          const found = findLabel(f.subcategories);
          if (found) return found;
        }
      }
      return key;
    };

    return findLabel(planFeatures);
  };

  // Use featureData prop first, fall back to planPricingData (API fetch)
  const getFeaturePrice = (key) => {
    const source = featureData && Array.isArray(featureData) && featureData.length
      ? featureData
      : planPricingData;
    if (!source || !Array.isArray(source)) return undefined;
    const feature = source.find(f => f.feature_name === key);
    return feature ? parseFloat(feature.price) : undefined;
  };

  const oldChargeMap = {};
  (planData?.feature_snapshot || []).forEach((f) => {
    const key = f.feature_name || f.feature_label;
    if (key) {
      oldChargeMap[key] = Number(f.charge ?? f.price ?? 0);
    }
  });

  const getPriceForBadge = (key) => {
    const newPrice = getFeaturePrice(key);
    if (newPrice == null || Number(newPrice) <= 0) return null;

    const oldPrice = oldChargeMap[key];

    if (id && oldPrice != null && Number(oldPrice) !== Number(newPrice)) {
      return {
        changed: true,
        text: `$${Number(oldPrice).toFixed(2)} → $${Number(newPrice).toFixed(2)}`,
      };
    }

    return {
      changed: false,
      value: Number(newPrice),
    };
  };

  const calculateSelectedPrices = () => {
    const source = featureData && Array.isArray(featureData) && featureData.length
      ? featureData
      : planPricingData;
    if (!source || !Array.isArray(source)) {
      return { selected: [], totalPrice: 0 };
    }

    const selected = [];
    let totalPrice = 0;

    source.forEach((feature) => {
      const key = feature.feature_name;
      const price = parseFloat(feature.price) || 0;
      if (formik.values[key] && price > 0) {
        selected.push({
          key,
          name: getFeatureLabel(key),
          price,
        });
        totalPrice += price;
      }
    });

    return { selected, totalPrice };
  };

  const { selected = [], totalPrice = 0 } = calculateSelectedPrices();

  const changedExtraChargeFeatures = [
    ...(planData?.feature_snapshot || []).map((oldFeature) => {
      const currentFeature = selected.find(
        (item) => item.key === oldFeature.feature_name
      );

      const oldCharge = Number(
        oldFeature?.charge ?? oldFeature?.price ?? 0
      );

      const newCharge = currentFeature
        ? Number(currentFeature.price || 0)
        : 0;

      if (oldCharge === newCharge) return null;

      return {
        name:
          currentFeature?.name ||
          oldFeature.feature_label ||
          oldFeature.feature_name,

        oldCharge,
        newCharge,
        action: newCharge > oldCharge ? "Enabled" : "Removed",
      };
    }),

    ...selected
      .filter(
        (feature) =>
          !(planData?.feature_snapshot || []).some(
            (old) => old.feature_name === feature.key
          )
      )
      .map((feature) => ({
        name: feature.name,
        oldCharge: 0,
        newCharge: Number(feature.price || 0),
        action: "Enabled",
      })),
  ].filter(Boolean);

  const isExtraChargeUpdated =
    id &&
    planData?.is_extra_charges != null &&
    Number(planData.is_extra_charges) !== Number(totalPrice);

  const enabledFeatureCount = planFeatures.filter((f) => formik.values[f.key]).length;

  const filteredPlanFeatures = useMemo(() => {
    const q = featureSearch.trim().toLowerCase();

    return planFeatures.filter((feature) => {
      const isOn = !!formik.values[feature.key];
      if (featureFilter === "enabled" && !isOn) return false;
      if (featureFilter === "disabled" && isOn) return false;

      if (!q) return true;

      const haystack = [
        feature.label,
        feature.description,
        ...(feature.subcategories || []).flatMap((sub) => [
          sub.label,
          sub.description,
          ...(sub.subcategories || []).map((nested) => nested.label),
        ]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [planFeatures, formik.values, featureSearch, featureFilter]);

  useEffect(() => {
    if (!filteredPlanFeatures.length) return;
    if (!filteredPlanFeatures.some((f) => f.key === activeFeatureKey)) {
      setActiveFeatureKey(filteredPlanFeatures[0].key);
    }
  }, [filteredPlanFeatures, activeFeatureKey]);

  const activeFeature =
    filteredPlanFeatures.find((f) => f.key === activeFeatureKey) ||
    planFeatures.find((f) => f.key === activeFeatureKey) ||
    planFeatures[0];

  const renderPriceBadge = (key) => {
    const info = getPriceForBadge(key);
    if (!info) return null;

    if (info.changed) {
      return (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            bgcolor: "#fff7ed",
            border: "1px solid #fed7aa",
            px: 0.85,
            py: 0.15,
            borderRadius: "999px",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#c2410c !important", fontWeight: 700, fontSize: 10.5, lineHeight: 1.2 }}
          >
            +{info.text}
          </Typography>
        </Box>
      );
    }

    return <PriceBadge price={info.value} />;
  };

  const handleFeatureToggle = (feature, checked) => {
    formik.setFieldValue(feature.key, checked);
    formik.setFieldTouched(feature.key, true, false);

    if (feature.key === "accommodation") {
      if (formik.values.tour_management) {
        formik.setFieldValue("tour_accommodation", checked);
      }
      if (formik.values.event_management) {
        formik.setFieldValue("event_accommodation", checked);
      }

      if (!checked) {
        formik.setFieldValue("accommodation_add_on", false);
        formik.setFieldValue("tour_accommodation_add_on", false);
        formik.setFieldValue("event_accommodation_add_on", false);
      }
    }

    if (feature.key === "product_management") {
      if (formik.values.tour_management) {
        formik.setFieldValue("tour_product", checked);
      }

      if (formik.values.event_management) {
        formik.setFieldValue("event_product", checked);
      }

      if (!checked) {
        formik.setFieldValue("product_add_on", false);
        formik.setFieldValue("self_purchase", false);
        formik.setFieldValue("product_supplier", false);
        formik.setFieldValue("product_sell_directly", false);

        formik.setFieldValue("tour_sell_product_directly", false);
        formik.setFieldValue("sell_product_directly", false);

        formik.setFieldValue("tour_add_on_tour_event", false);
        formik.setFieldValue("add_on_tour_event", false);
      }
    }

    if (!feature.subcategories?.length) return;

    if (!checked && feature.subcategories) {
      feature.subcategories.forEach((sub) => {
        formik.setFieldValue(sub.key, false);

        sub.subcategories?.forEach((nested) => {
          formik.setFieldValue(nested.key, false);
          nested.subcategories?.forEach((deepNested) => {
            formik.setFieldValue(deepNested.key, false);
          });
        });

        if (sub.key === "tour_accommodation" || sub.key === "event_accommodation") {
          const otherAccommodationEnabled =
            (sub.key === "tour_accommodation" && formik.values.event_accommodation) ||
            (sub.key === "event_accommodation" && formik.values.tour_accommodation);

          if (!otherAccommodationEnabled) {
            formik.setFieldValue("accommodation", false);
          }
        }

        if (sub.subcategories?.some(
          (nested) => nested.key === "tour_accommodation_add_on" || nested.key === "event_accommodation_add_on"
        )) {
          const otherAddOnEnabled =
            feature.key === "tour_management"
              ? formik.values.event_accommodation_add_on
              : formik.values.tour_accommodation_add_on;

          if (!otherAddOnEnabled) {
            formik.setFieldValue("accommodation_add_on", false);
          }
        }
      });
    }

    if (
      !checked &&
      (feature.key === "tour_management" || feature.key === "event_management")
    ) {
      const hasAnyProduct =
        feature.key === "tour_management"
          ? formik.values.event_product
          : formik.values.tour_product;

      if (!hasAnyProduct) {
        formik.setFieldValue("product_management", false);
        formik.setFieldValue("product_add_on", false);
        formik.setFieldValue("self_purchase", false);
        formik.setFieldValue("tour_sell_product_directly", false);
        formik.setFieldValue("sell_product_directly", false);
        formik.setFieldValue("tour_add_on_tour_event", false);
        formik.setFieldValue("add_on_tour_event", false);
      }

      const hasAnyAccommodation =
        feature.key === "tour_management"
          ? formik.values.event_accommodation
          : formik.values.tour_accommodation;

      if (!hasAnyAccommodation) {
        formik.setFieldValue("accommodation", false);
        formik.setFieldValue("accommodation_add_on", false);
        formik.setFieldValue("tour_accommodation_add_on", false);
        formik.setFieldValue("event_accommodation_add_on", false);
      }
    }
  };

  const handleSubToggle = (sub, checked) => {
    formik.setFieldValue(sub.key, checked);

    if (sub.key === "accommodation_add_on") {
      if (formik.values.tour_management && formik.values.tour_accommodation) {
        formik.setFieldValue("tour_accommodation_add_on", checked);
      }
      if (formik.values.event_management && formik.values.event_accommodation) {
        formik.setFieldValue("event_accommodation_add_on", checked);
      }
    }

    if (sub.key === "self_purchase" && !checked) {
      formik.setFieldValue("tour_sell_product_directly", false);
      formik.setFieldValue("sell_product_directly", false);
    }

    if (sub.key === "product_add_on" && !checked) {
      formik.setFieldValue("tour_add_on_tour_event", false);
      formik.setFieldValue("add_on_tour_event", false);
    }

    if (sub.key === "self_purchase" && checked) {
      if (formik.values.tour_management && formik.values.tour_product) {
        formik.setFieldValue("tour_sell_product_directly", true);
      }
      if (formik.values.event_management && formik.values.event_product) {
        formik.setFieldValue("sell_product_directly", true);
      }
    }

    if (sub.key === "product_add_on" && checked) {
      if (formik.values.tour_management && formik.values.tour_product) {
        formik.setFieldValue("tour_add_on_tour_event", true);
      }
      if (formik.values.event_management && formik.values.event_product) {
        formik.setFieldValue("add_on_tour_event", true);
      }
    }

    if ((sub.key === "tour_accommodation" || sub.key === "event_accommodation") && checked) {
      formik.setFieldValue("accommodation", true);
    }

    if ((sub.key === "tour_product" || sub.key === "event_product") && checked) {
      formik.setFieldValue("product_management", true);
    }

    if (!checked && sub.subcategories) {
      sub.subcategories.forEach((nested) => {
        formik.setFieldValue(nested.key, false);
      });
    }

    if ((sub.key === "tour_accommodation" || sub.key === "event_accommodation") && !checked) {
      const otherAccommodationEnabled =
        (sub.key === "tour_accommodation" && formik.values.event_accommodation) ||
        (sub.key === "event_accommodation" && formik.values.tour_accommodation);

      if (!otherAccommodationEnabled) {
        formik.setFieldValue("accommodation", false);
        formik.setFieldValue("accommodation_add_on", false);
      }
    }

    if ((sub.key === "tour_product" || sub.key === "event_product") && !checked) {
      const otherProductEnabled =
        (sub.key === "tour_product" && formik.values.event_product) ||
        (sub.key === "event_product" && formik.values.tour_product);

      if (!otherProductEnabled) {
        formik.setFieldValue("product_management", false);
        formik.setFieldValue("product_add_on", false);
        formik.setFieldValue("self_purchase", false);
      }
    }
  };

  const handleNestedToggle = (nested, checked) => {
    formik.setFieldValue(nested.key, checked);

    if (nested.key === "tour_accommodation_add_on" || nested.key === "event_accommodation_add_on") {
      if (checked) {
        formik.setFieldValue("accommodation_add_on", true);
      } else {
        const otherAddOnEnabled =
          (nested.key === "tour_accommodation_add_on" && formik.values.event_accommodation_add_on) ||
          (nested.key === "event_accommodation_add_on" && formik.values.tour_accommodation_add_on);

        if (!otherAddOnEnabled) {
          formik.setFieldValue("accommodation_add_on", false);
        }
      }
    }

    if (
      nested.key === "tour_sell_product_directly" ||
      nested.key === "sell_product_directly"
    ) {
      if (checked) {
        formik.setFieldValue("self_purchase", true);
      } else {
        const otherEnabled =
          (nested.key === "tour_sell_product_directly" && formik.values.sell_product_directly) ||
          (nested.key === "sell_product_directly" && formik.values.tour_sell_product_directly);

        if (!otherEnabled) {
          formik.setFieldValue("self_purchase", false);
        }
      }
    }

    if (
      nested.key === "tour_add_on_tour_event" ||
      nested.key === "add_on_tour_event"
    ) {
      if (checked) {
        formik.setFieldValue("product_add_on", true);
      } else {
        const otherEnabled =
          (nested.key === "tour_add_on_tour_event" && formik.values.add_on_tour_event) ||
          (nested.key === "add_on_tour_event" && formik.values.tour_add_on_tour_event);

        if (!otherEnabled) {
          formik.setFieldValue("product_add_on", false);
        }
      }
    }
  };

  // Console log whenever feature selections change
  useEffect(() => {
    const { selected: items, totalPrice: total } = calculateSelectedPrices();

    setExtraCharge(total);
    console.groupEnd();
  }, [JSON.stringify(formik.values)]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        className="text-bg-primary"
        sx={{
          py: 2.5,
          px: 3,
          position: "relative",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: 1.2,
            color: "#fff",
          }}
        >
          {id ? "Update Tripola Subscription Plan" : "Create Tripola Subscription Plan"}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "rgba(255,255,255,0.8)",
            mt: 0.5,
            fontSize: "13px",
          }}
        >
          {id
            ? "Update plan details, pricing and features."
            : "Configure pricing, billing and available features."}
        </Typography>

        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#fff",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {fetching ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={400}
        >
          <CircularProgress />
        </Box>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <DialogContent sx={{ px: 3, py: 3 }}>
            {/* Plan Information Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
              }}
            >
              <SectionHeader title="Plan Information" />
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Plan Name<span className="text-danger ms-1">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter plan name"
                    name="plan_name"
                    size="small"
                    fullWidth
                    disabled={id}
                    value={formik.values.plan_name}
                    onChange={handleTextChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.plan_name &&
                      Boolean(formik.errors.plan_name)
                    }
                    helperText={
                      formik.touched.plan_name && formik.errors.plan_name
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Plan Description<span className="text-danger ms-1">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter description"
                    name="plan_description"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    value={formik.values.plan_description}
                    onChange={handleTextChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.plan_description &&
                      Boolean(formik.errors.plan_description)
                    }
                    helperText={
                      formik.touched.plan_description &&
                      formik.errors.plan_description
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Targeted Users<span className="text-danger ms-1">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter targeted users description"
                    name="targeted_users"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={formik.values.targeted_users}
                    onChange={handleTextChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.targeted_users &&
                      Boolean(formik.errors.targeted_users)
                    }
                    helperText={
                      formik.touched.targeted_users &&
                      formik.errors.targeted_users
                    }
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Plan Status Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
              }}
            >
              <SectionHeader title="Plan Status" />
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.is_active}
                    onChange={(e) =>
                      formik.setFieldValue("is_active", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {formik.values.is_active ? "Active" : "Inactive"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formik.values.is_active
                        ? "This plan is available for users to subscribe"
                        : "This plan is hidden from users"}
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            {/* Plan Features Section */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                mb: 3,
                bgcolor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.08)",
                borderRadius: "16px",
                boxShadow: "0 4px 6px -1px rgba(15, 23, 42, 0.04), 0 12px 28px -8px rgba(15, 23, 42, 0.08)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#0f172a",
                      letterSpacing: "-0.01em",
                      mb: 0.35,
                    }}
                  >
                    Plan Features
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#64748b", lineHeight: 1.45 }}>
                    Pick a module on the left, enable it, then toggle options on the right.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "10px",
                    bgcolor: enabledFeatureCount > 0 ? "#eef2ff" : "#f8fafc",
                    color: enabledFeatureCount > 0 ? "#1e3a8a" : "#64748b",
                    border: enabledFeatureCount > 0
                      ? "1px solid rgba(34, 65, 158, 0.15)"
                      : "1px solid rgba(15, 23, 42, 0.06)",
                    fontSize: 12.5,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {enabledFeatureCount}/{planFeatures.length} enabled
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1, mb: 1.75, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Search modules or options..."
                  value={featureSearch}
                  onChange={(e) => setFeatureSearch(e.target.value)}
                  sx={{
                    flex: "1 1 220px",
                    minWidth: 180,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      bgcolor: "#f8fafc",
                      fontSize: 13.5,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: "inline-flex", gap: 0.5, p: 0.4, borderRadius: "10px", bgcolor: "#f1f5f9" }}>
                  {[
                    { key: "all", label: "All" },
                    { key: "enabled", label: "Enabled" },
                    { key: "disabled", label: "Disabled" },
                  ].map((tab) => {
                    const active = featureFilter === tab.key;
                    return (
                      <Box
                        key={tab.key}
                        component="button"
                        type="button"
                        onClick={() => setFeatureFilter(tab.key)}
                        sx={{
                          border: "none",
                          cursor: "pointer",
                          px: 1.35,
                          py: 0.7,
                          borderRadius: "8px",
                          fontSize: 12.5,
                          fontWeight: 700,
                          bgcolor: active ? "#22419e" : "transparent",
                          color: active ? "#fff" : "#64748b",
                          transition: "background 0.15s ease, color 0.15s ease",
                        }}
                      >
                        {tab.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "220px 1fr" },
                  gap: 0,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: "14px",
                  overflow: "hidden",
                  minHeight: { md: 420 },
                }}
              >
                {/* Left: modules */}
                <Box
                  sx={{
                    borderRight: { md: "1px solid rgba(15, 23, 42, 0.08)" },
                    borderBottom: { xs: "1px solid rgba(15, 23, 42, 0.08)", md: "none" },
                    bgcolor: "#f8fafc",
                    maxHeight: { xs: 220, md: 480 },
                    overflowY: "auto",
                  }}
                >
                  {filteredPlanFeatures.length === 0 ? (
                    <Box sx={{ p: 2.5, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                        No modules match your search.
                      </Typography>
                    </Box>
                  ) : (
                    filteredPlanFeatures.map((feature) => {
                      const isActive = activeFeature?.key === feature.key;
                      const isOn = !!formik.values[feature.key];
                      const selectedSubs = (feature.subcategories || []).filter(
                        (sub) => formik.values[sub.key]
                      ).length;
                      const totalSubs = feature.subcategories?.length || 0;

                      return (
                        <Box
                          key={feature.key}
                          component="button"
                          type="button"
                          onClick={() => setActiveFeatureKey(feature.key)}
                          sx={{
                            width: "100%",
                            textAlign: "left",
                            border: "none",
                            borderLeft: "3px solid",
                            borderLeftColor: isActive ? "#22419e" : "transparent",
                            bgcolor: isActive ? "#ffffff" : "transparent",
                            px: 1.5,
                            py: 1.15,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            transition: "background 0.15s ease",
                            "&:hover": { bgcolor: isActive ? "#ffffff" : "#eef2ff" },
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: isOn ? "#22c55e" : "#cbd5e1",
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: isActive ? 700 : 600,
                                color: isActive ? "#22419e" : "#334155",
                                lineHeight: 1.3,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {feature.label}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.15 }}>
                              {isOn
                                ? totalSubs
                                  ? `${selectedSubs}/${totalSubs} options`
                                  : "Enabled"
                                : "Off"}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>

                {/* Right: active module */}
                <Box sx={{ bgcolor: "#ffffff", display: "flex", flexDirection: "column", minHeight: 280 }}>
                  {activeFeature ? (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
                          bgcolor: formik.values[activeFeature.key] ? "#f8faff" : "#ffffff",
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                              {activeFeature.label}
                            </Typography>
                            {renderPriceBadge(activeFeature.key)}
                            <Box
                              sx={{
                                px: 0.9,
                                py: 0.2,
                                borderRadius: "999px",
                                fontSize: 10.5,
                                fontWeight: 700,
                                bgcolor: formik.values[activeFeature.key] ? "#dcfce7" : "#f1f5f9",
                                color: formik.values[activeFeature.key] ? "#15803d" : "#64748b",
                              }}
                            >
                              {formik.values[activeFeature.key] ? "Enabled" : "Off"}
                            </Box>
                          </Box>
                          {activeFeature.description && (
                            <Typography
                              sx={{
                                mt: 0.35,
                                fontSize: 12.5,
                                color: "#64748b",
                                lineHeight: 1.4,
                                display: { xs: "none", sm: "block" },
                              }}
                            >
                              {activeFeature.description}
                            </Typography>
                          )}
                        </Box>
                        <Switch
                          disableRipple
                          checked={!!formik.values[activeFeature.key]}
                          onChange={(e) => handleFeatureToggle(activeFeature, e.target.checked)}
                          sx={featureSwitchSx}
                        />
                      </Box>

                      {!formik.values[activeFeature.key] ? (
                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
                          <Box sx={{ textAlign: "center", maxWidth: 280 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                              Enable this module
                            </Typography>
                            <Typography sx={{ mt: 0.6, fontSize: 13, color: "#94a3b8", lineHeight: 1.5 }}>
                              Turn the switch on to configure its options.
                            </Typography>
                          </Box>
                        </Box>
                      ) : (activeFeature.subcategories?.length > 0) ? (
                        <Box sx={{ flex: 1, overflowY: "auto", maxHeight: { md: 400 }, p: 1.25 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              px: 0.75,
                              mb: 0.75,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                color: "#94a3b8",
                              }}
                            >
                              Options
                              {" · "}
                              {
                                (activeFeature.subcategories || []).filter((sub) => formik.values[sub.key]).length
                              }
                              /{(activeFeature.subcategories || []).length}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Box
                                component="button"
                                type="button"
                                onClick={() => {
                                  (activeFeature.subcategories || []).forEach((sub) => {
                                    handleSubToggle(sub, true);
                                    (sub.subcategories || []).forEach((nested) =>
                                      handleNestedToggle(nested, true)
                                    );
                                  });
                                }}
                                sx={{
                                  border: "none",
                                  bgcolor: "transparent",
                                  color: "#22419e",
                                  fontSize: 11.5,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  px: 0.75,
                                  py: 0.35,
                                  borderRadius: "6px",
                                  "&:hover": { bgcolor: "#eef2ff" },
                                }}
                              >
                                Select all
                              </Box>
                              <Box
                                component="button"
                                type="button"
                                onClick={() => {
                                  (activeFeature.subcategories || []).forEach((sub) => {
                                    handleSubToggle(sub, false);
                                  });
                                }}
                                sx={{
                                  border: "none",
                                  bgcolor: "transparent",
                                  color: "#64748b",
                                  fontSize: 11.5,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  px: 0.75,
                                  py: 0.35,
                                  borderRadius: "6px",
                                  "&:hover": { bgcolor: "#f1f5f9" },
                                }}
                              >
                                Clear
                              </Box>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              border: "1px solid rgba(15, 23, 42, 0.06)",
                              borderRadius: "12px",
                              overflow: "hidden",
                              bgcolor: "#fff",
                            }}
                          >
                            {((activeFeature.subcategories || [])).map((sub, idx) => {
                              const subEnabled = !!formik.values[sub.key];
                              const hasNested = Array.isArray(sub.subcategories) && sub.subcategories.length > 0;

                              return (
                                <Box key={sub.key}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      px: 1.25,
                                      py: 0.85,
                                      minHeight: 42,
                                      borderTop: idx === 0 ? "none" : "1px solid rgba(15, 23, 42, 0.05)",
                                      bgcolor: subEnabled ? "rgba(34, 65, 158, 0.03)" : "transparent",
                                      cursor: "pointer",
                                      "&:hover": { bgcolor: "#f8fafc" },
                                    }}
                                    onClick={() => handleSubToggle(sub, !subEnabled)}
                                    title={sub.description || sub.label}
                                  >
                                    <Typography
                                      sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: 13.25,
                                        fontWeight: 600,
                                        color: "#1e293b",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {sub.label}
                                    </Typography>
                                    {renderPriceBadge(sub.key)}
                                    <Switch
                                      disableRipple
                                      checked={subEnabled}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => handleSubToggle(sub, e.target.checked)}
                                      sx={featureSwitchSmallSx}
                                    />
                                  </Box>

                                  {hasNested ? (
                                    <Collapse in={subEnabled} timeout={160} unmountOnExit>
                                      <Box sx={{ bgcolor: "#f8fafc", borderTop: "1px solid rgba(15, 23, 42, 0.04)" }}>
                                        {(sub.subcategories || []).map((nested) => {
                                          const nestedEnabled = !!formik.values[nested.key];
                                          return (
                                            <Box
                                              key={nested.key}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                pl: 2.5,
                                                pr: 1.25,
                                                py: 0.65,
                                                minHeight: 38,
                                                borderTop: "1px solid rgba(15, 23, 42, 0.04)",
                                                cursor: "pointer",
                                                "&:hover": { bgcolor: "#eef2ff" },
                                              }}
                                              onClick={() => handleNestedToggle(nested, !nestedEnabled)}
                                              title={nested.description || nested.label}
                                            >
                                              <Typography
                                                sx={{
                                                  flex: 1,
                                                  minWidth: 0,
                                                  fontSize: 12.75,
                                                  fontWeight: 500,
                                                  color: "#475569",
                                                  whiteSpace: "nowrap",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                }}
                                              >
                                                {nested.label}
                                              </Typography>
                                              {renderPriceBadge(nested.key)}
                                              <Switch
                                                disableRipple
                                                checked={nestedEnabled}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) =>
                                                  handleNestedToggle(nested, e.target.checked)
                                                }
                                                sx={featureSwitchSmallSx}
                                              />
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Collapse>
                                  ) : null}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            flex: 1,
                            minHeight: 260,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 4,
                            bgcolor: "#ffffff",
                          }}
                        >
                          <Box sx={{ textAlign: "center", maxWidth: 340 }}>
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                mx: "auto",
                                mb: 1.5,
                                borderRadius: "16px",
                                bgcolor: "rgba(34, 65, 158, 0.08)",
                                color: "#22419e",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CheckCircleOutlineIcon sx={{ fontSize: 30 }} />
                            </Box>
                            <Typography
                              sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}
                            >
                              {activeFeature.label} is active
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.75,
                                fontSize: 13,
                                color: "#64748b",
                                lineHeight: 1.55,
                              }}
                            >
                              No extra settings for this module. Use the switch
                              above to turn it on or off for the tenant.
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {formik.touched[activeFeature.key] && formik.errors[activeFeature.key] && (
                        <Typography color="error" variant="caption" sx={{ px: 2, pb: 1.5, display: "block" }}>
                          {formik.errors[activeFeature.key]}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                        Select a module to configure.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>

            {/* Pricing Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
              }}
            >
              <SectionHeader title="Pricing" />
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Monthly Price<span className="text-danger ms-1">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter monthly price"
                    name="monthly_price"
                    type="number"
                    size="small"
                    fullWidth
                    value={formik.values.monthly_price}
                    onChange={handleNumberChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.monthly_price &&
                      Boolean(formik.errors.monthly_price)
                    }
                    helperText={
                      formik.touched.monthly_price &&
                      formik.errors.monthly_price
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Yearly Price<span className="text-danger ms-1">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter yearly price"
                    name="yearly_price"
                    type="number"
                    size="small"
                    fullWidth
                    value={formik.values.yearly_price}
                    onChange={handleNumberChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.yearly_price &&
                      Boolean(formik.errors.yearly_price)
                    }
                    helperText={
                      formik.touched.yearly_price && formik.errors.yearly_price
                    }
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Extra Charges Summary */}
            <Paper
              elevation={0}
              sx={{
                mb: 1,
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid rgba(15, 23, 42, 0.08)",
                background: "#ffffff",
                boxShadow: "0 4px 6px -1px rgba(15, 23, 42, 0.04), 0 12px 28px -8px rgba(15, 23, 42, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#eef2ff",
                    color: "#22419e",
                    flexShrink: 0,
                  }}
                >
                  <PaidOutlinedIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#0f172a",
                      letterSpacing: "-0.01em",
                      lineHeight: 1.3,
                    }}
                  >
                    Extra Charges
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: "#94a3b8", mt: 0.25 }}>
                    {isExtraChargeUpdated
                      ? "This amount will be applicable after the plan is updated"
                      : "This amount will be charged based on selected features"}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1, minWidth: 170 }}>
                {isExtraChargeUpdated && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#475569", fontSize: 13 }}>
                    <Typography>Was: $ {Number(planData.is_extra_charges ?? 0).toFixed(2)}</Typography>
                    <Typography>→</Typography>
                  </Box>
                )}

                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#22419e",
                    letterSpacing: "-0.02em",
                    whiteSpace: "nowrap",
                  }}
                >
                  $ {totalPrice.toFixed(2)}
                </Typography>
              </Box>
            </Paper>

          </DialogContent>
          <DialogActions className="border-top p-7 d-flex justify-content-between">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-sm btn-outline btn-outline-primary"
              disabled={loading}
            >
              Cancel
            </button>

            <button type="submit" className="btn btn-sm btn-primary">
              {id ? "Update" : "Create"} Plan
              {loading && (
                <span
                  className="spinner-border spinner-border-sm ms-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
            </button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default AddEditTripolaPlan;